import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Company } from '../models/company';
import { User } from '../models/user';
import { CacheableResourceService } from './cacheable-resource-service';
import { ProviderService } from './v2/provider.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyService extends CacheableResourceService<Company> {
  private currentCompany$: Subject<Company> = new ReplaySubject<Company>(1);
  public adminApiUrl = '/api/admin/companies';

  constructor(
    httpClient: HttpClient,
    private providerService: ProviderService
  ) {
    super(httpClient, '/api/company');
  }

  getAssociatedUsers(
    companyKey: string,
    companyType?: string
  ): Observable<User[]> {
    return this.httpClient.get<User[]>(
      `${this.apiUrl}/${companyKey}/users${
        companyType ? '?type=' + companyType : ''
      }`
    );
  }

  getTotalLockedCompanies() {
    return this.httpClient.get<number>(`${this.apiUrl}/in-progress/count`);
  }

  unlockProcessingCompanies() {
    return this.httpClient.put<any>(`${this.apiUrl}/in-progress/mark-unlock`, []);
  }

  unlockProcessingCompany(company: Company) {
    return this.httpClient.put<any>(`${this.apiUrl}/in-progress/${company.companyKey}`, [company]);
  }

  getById(id: any, makeCurrent: boolean = false): Observable<Company> {
    if (!id || id === '0') {
      return of(null);
    }

    if (makeCurrent) {
      return super.getById(id, makeCurrent).pipe(
        tap((company) => {
          this.cache['currentCompany'] = company;
          this.currentCompany$.next(company);
        })
      );
    } else {
      return super.getById(id);
    }
  }

  updateCompanyInfo(company: Partial<Company>): Observable<Company> {
    return this.httpClient.put<Company>(
      `${this.apiUrl}/${company.companyKey}`,
      company
    );
  }

  lockCompany(company: Company, isLocked: boolean): Observable<Company> {
    return this.httpClient.put<Company>(
      `${this.adminApiUrl}/${company.companyKey}/${company.companyType}/lock`,
      { isLocked }
    );
  }

  currentCompany(): Company {
    return this.cache['currentCompany'];
  }

  getCurrentCompany(): Observable<Company> {
    return this.currentCompany$;
  }

  getNewById(id: any): Observable<Company> {
    return this.httpClient.get<Company>(`${this.apiUrl}/${id}`);
  }

  updateCacheCompany(company: Company): void {
    this.cache['currentCompany'] = company;
    this.currentCompany$.next(company);
  }

  clearCompany(): void {
    this.clearCache();
    this.currentCompany$ = new ReplaySubject<Company>(1);
  }

  deleteCompanyForAdmin(
    { companyKey, companyType }: Company,
    deleteType: string
  ): Observable<any> {
    const httpDeleteCompany = this.httpClient.delete<any>(
      `${this.adminApiUrl}/${companyKey}/${companyType}?deleteType=${deleteType}`
    );

    return forkJoin([
      httpDeleteCompany,
      this.providerService.deleteProvider(companyKey, deleteType),
    ]);
  }

  getCompanyByIdForAdmin(companyKey, companyType): Observable<Company> {
    return this.httpClient.get<Company>(
      `${this.adminApiUrl}/${companyKey}/${companyType}`
    );
  }

  setCompanyGridState(state: any) {
    this.cache['companyGridState'] = state;
  }

  getCompanyGridState() {
    return this.cache['companyGridState'];
  }

  // Re-classify this company's already-synced ShipStation bundles against its current
  // shipStationBundleMode (proxied to API v2). Save the company first so the flag is persisted.
  applyShipStationCasePackConversion(
    companyKey: string
  ): Observable<{ kitCount: number; casePackCount: number }> {
    return this.httpClient.post<{ kitCount: number; casePackCount: number }>(
      '/api/ship-station/apply-case-pack-conversion',
      { companyId: companyKey }
    );
  }
}
